import { useEffect, useState, useCallback } from 'react';
import { useDebounce } from 'use-debounce';

export interface FormData {
    programId: string;
    title: string;
    description: string;
    thumbnail: string;
    content: string;
    isTrial: boolean;
    isVisible: boolean;
}

type UseReferenceFormProps = {
    initialFormData: FormData;
    debounceDelay?: number;
};

export function useReferenceForm({ initialFormData, debounceDelay = 300 }: UseReferenceFormProps) {
// Form data state from the api call
    const [formData, setFormData] = useState<FormData>(initialFormData);

    // Raw form debouncing and data or the dirty form data once the user
    // filled out information in textfields
    const [rawFormData, setRawFormData] = useState<FormData>(initialFormData);
    const [debouncedFormData] = useDebounce(rawFormData, debounceDelay);

    // Touch fields recording to handle error textfield validation
    const [touchedFields, setTouchedFields] = useState<Record<keyof FormData, boolean>>({
        programId: false,
        title: false,
        description: false,
        thumbnail: false,
        content: false,
        isTrial: false,
        isVisible: false
    });

    // Debouncing mechanism for form state (create / edit)
    useEffect(() => {
        setFormData(debouncedFormData);
    }, [debouncedFormData]);

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const { name, value, type, checked } = e.target as HTMLInputElement;
            setRawFormData((prev) => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value,
            }));
        },
        [],
    );
    const handleBlur = useCallback(
        (field: keyof FormData) => {
            setTouchedFields((prev) => ({ ...prev, [field]: true }));
        },
        [],
    );

    const resetForm = useCallback((newInitialData?: FormData) => {
        const resetData = newInitialData || initialFormData;
        setRawFormData(resetData);
        setFormData(resetData);
        setTouchedFields({
            programId: false,
            title: false,
            description: false,
            thumbnail: false,
            content: false,
            isTrial: false,
            isVisible: false
        });
    }, [initialFormData]);

    const isFormValid = useCallback(() => {
        return (
            !!formData.programId?.trim() &&
            !!formData.title?.trim() &&
            !!formData.description?.trim() &&
            !!formData.thumbnail?.trim() &&
            !!formData.content?.trim() &&
            formData.isTrial !== undefined &&
            formData.isVisible !== undefined
        );
    }, [formData]);

    return {
        formData,
        rawFormData,
        setRawFormData,
        setFormData,
        touchedFields,
        setTouchedFields,
        handleChange,
        handleBlur,
        resetForm,
        isFormValid,
    };
}
